import {
	Accordion,
	getKeys,
	Heading,
	moneyFormatNoDecimal,
	P,
	Stack,
	Text,
	useText,
} from "@sveabank/cc-common";
import type { SelectedPackage } from "../../types/package";
import { isSelectedProductCreditProduct, type SelectedProduct } from "../../types/product";
import BorderDivider from "../border-divider";
import BulletList from "../bullet-list";
import BulletListItem from "../bullet-list/list-item";
import Card from "../card";
import { calculatePackageSum, getSelectedAddonsForReview } from "./helpers";
import ProductItemContent from "./product-content";
import ProductItemHeading from "./product-heading";
import usePackageTitle from "./usePackageTitle";

type Props = {
	selectedPackage: SelectedPackage;
};

export type SelectedAddonsRecord = Record<
	SelectedProduct["productDefinitionId"],
	{ count: number; product: SelectedProduct }
>;

const ProductSection = ({ selectedPackage }: Props) => {
	const [t, texts] = useText();

	const packageTitle = usePackageTitle(selectedPackage.packageId);
	const reviewProducts = getSelectedAddonsForReview(selectedPackage.products);
	const keys = getKeys(reviewProducts);

	return (
		<Stack gap={4}>
			<Heading.H3>{t(texts.signPage.package.products.title)}</Heading.H3>
			<BorderDivider>
				<Stack gap={4}>
					<Heading.H4>
						<Stack direction="row">
							<span className="flex-1">{packageTitle}</span>
							<span>{`${moneyFormatNoDecimal(
								Number(calculatePackageSum(selectedPackage)),
								true,
							)}${t(texts.common.perMonth)}`}</span>
						</Stack>
					</Heading.H4>
					<BulletList variant="small">
						{selectedPackage.products
							.filter((product) => !product.isAddon)
							.map((product) => (
								<Stack key={product.sku}>
									<BulletListItem variant="small" icon="CHECKMARK">
										{t(texts.addons[product.sku]?.title) || product.productDefinitionId}
									</BulletListItem>
									{isSelectedProductCreditProduct(product) && (
										<Text className="pb-2 pl-6" key={product.productId}>
											{t(
												texts.summaryPage.creditLimit,
												moneyFormatNoDecimal(+product.chosenCredit.amount, true),
											)}
										</Text>
									)}
								</Stack>
							))}
					</BulletList>
				</Stack>
			</BorderDivider>
			{keys.length > 0 && (
				<BorderDivider>
					<Stack gap={4}>
						<Heading.H4 className="text-text-headline">
							{t(texts.signPage.package.products.subTitle)}
						</Heading.H4>
						<BulletList variant="small">
							<Stack gap={1}>
								{keys.map((product) => (
									<Card
										classNames="bg-background-tertiary"
										direction="col"
										key={product}
										fullWidth
										boxShadow={false}
									>
										<Stack direction="col">
											{product === "CHECKKREDIT" || product === "CREDITCARD" ? (
												<Accordion>
													<Accordion.Heading>
														<ProductItemHeading product={product} selectedAddons={reviewProducts} />
													</Accordion.Heading>
													<Accordion.Content>
														<ProductItemContent
															product={product}
															selectedAddons={reviewProducts}
															selectedPackage={selectedPackage}
														/>
													</Accordion.Content>
												</Accordion>
											) : (
												<ProductItemHeading product={product} selectedAddons={reviewProducts} />
											)}
										</Stack>
									</Card>
								))}
							</Stack>
						</BulletList>
					</Stack>
				</BorderDivider>
			)}
			<BorderDivider>
				<Stack direction="row" align="center" justify="end" wrap gap={4}>
					<P>{t(texts.signPage.reviewPage.total)}</P>
					<Heading.H4 className="text-text-primary">{`${calculatePackageSum(selectedPackage, true)} ${t(
						texts.common.sekPerMonth,
					)}`}</Heading.H4>
				</Stack>
			</BorderDivider>
		</Stack>
	);
};

export default ProductSection;
